import React, {useEffect, useRef, useState} from 'react';
import {InputNumber} from 'antd';
import {
    AreaSeries,
    BaselineSeries,
    CandlestickSeries,
    ColorType,
    createChart,
    createSeriesMarkers,
    HistogramSeries,
    type IChartApi,
    type ISeriesApi,
    LineSeries,
    type SeriesMarker,
    type Time,
} from 'lightweight-charts';
import {getRealtimePrice, tradingService} from '../../../../services/tradingService';
import type {CompositeSignal, EvidenceZone} from '../../../../types/compositeSignal';

/**
 * 与 PatternRecognitionPage 图表完全一致的外观配色
 */
const CHART_COLORS = {
    bg: '#080b0f',
    text: '#3d5469',
    grid: '#1e2d3d',
    bull: '#00d97e',
    bear: '#ff5252',
    amber: '#f5a623',
    volUp: 'rgba(0,217,126,0.5)',
    volDown: 'rgba(255,82,82,0.5)',
    volDoji: 'rgba(245,166,35,0.5)',
};

// ── 指标配置（与 PatternRecognitionPage 模型一致） ──
type IndicatorPeriodConfig = { value: string; label: string; checked: boolean; color: string };
type IndicatorItemConfig = { key: string; label: string; pane: 'main' | 'sub'; periods: IndicatorPeriodConfig[] };

const DEFAULT_INDICATOR_CONFIGS: IndicatorItemConfig[] = [
    {
        key: 'SMA', label: 'SMA', pane: 'main', periods: [
            {value: '7', label: '7', checked: true, color: '#4d9fff'},
            {value: '25', label: '25', checked: true, color: '#a78bfa'},
        ]
    },
    {
        key: 'BOLL', label: 'BOLL', pane: 'main', periods: [
            {value: '20_2.0', label: '20,2', checked: true, color: '#f5a623'},
        ]
    },
    {
        key: 'RSI', label: 'RSI', pane: 'sub', periods: [
            {value: '14', label: '14', checked: true, color: '#00d97e'},
        ]
    },
    {
        key: 'MACD', label: 'MACD', pane: 'sub', periods: [
            {value: '12_26_9', label: '12,26,9', checked: true, color: '#4d9fff'},
        ]
    },
];

const PERIOD_ADD_COLORS = ['#4d9fff', '#a78bfa', '#f5a623', '#00d97e', '#ff5252', '#f5a623', '#2f54eb', '#13c2c2'];

interface CompositeKlinePanelProps {
    signal: CompositeSignal;
    apiKeyId?: number;
    onClose: () => void;
}

function toUtcSeconds(ts: number): number {
    return ts > 1e10 ? Math.floor(ts / 1000) : ts;
}

// ── 指标提取（与 PatternRecognitionPage 一致的纯函数逻辑） ──
function extractIndicatorLineData(
    indicators: any,
    indKey: string, mpvKeyPrefix: string, period: number
): { time: Time; value: number }[] {
    const ind = indicators?.[indKey];
    if (!ind?.values?.length) return [];
    const key = `${mpvKeyPrefix}_${period}`;
    const result: { time: Time; value: number }[] = [];
    for (const pt of ind.values) {
        const mpv = pt.multiPeriodValues;
        if (mpv && null != mpv[key]) {
            result.push({time: toUtcSeconds(pt.timestamp) as Time, value: mpv[key]});
        }
    }
    result.sort((a, b) => (a.time as number) - (b.time as number));
    return result;
}

function normalizeBollParam(value: string): string {
    const parts = value.replace(/^boll_/i, '').split(/[_,]/);
    if (parts.length < 2) return value;
    return `${Number(parts[0])}_${Number(parts[1])}`;
}

function indicatorsToBollData(indicators: any, requestedParam: string): {
    upper: { time: Time; value: number }[];
    middle: { time: Time; value: number }[];
    lower: { time: Time; value: number }[];
} | null {
    const boll = indicators?.['BOLL'];
    if (!boll?.values?.length) return null;
    let bollKey: string | null = null;
    for (const pt of boll.values) {
        if (pt.multiPeriodValues) {
            for (const k of Object.keys(pt.multiPeriodValues)) {
                if (k.startsWith('boll_') && normalizeBollParam(k) === normalizeBollParam(requestedParam)) {
                    bollKey = k;
                    break;
                }
            }
        }
        if (bollKey) break;
    }
    if (null == bollKey) return null;
    const upper: { time: Time; value: number }[] = [];
    const middle: { time: Time; value: number }[] = [];
    const lower: { time: Time; value: number }[] = [];
    for (const pt of boll.values) {
        const val = pt.multiPeriodValues?.[bollKey];
        if (!val || null == val.middle) continue;
        const t = toUtcSeconds(pt.timestamp) as Time;
        if (null != val.upper) upper.push({time: t, value: val.upper});
        if (null != val.middle) middle.push({time: t, value: val.middle});
        if (null != val.lower) lower.push({time: t, value: val.lower});
    }
    if (0 === upper.length) return null;
    upper.sort((a, b) => (a.time as number) - (b.time as number));
    middle.sort((a, b) => (a.time as number) - (b.time as number));
    lower.sort((a, b) => (a.time as number) - (b.time as number));
    return {upper, middle, lower};
}

function normalizeMacdKey(raw: string): string {
    return raw.replace(/^macd_/, '').replace(/[,;:_]/g, ',');
}

function indicatorsToMacdData(indicators: any, macdParamKey: string): {
    dif: { time: Time; value: number }[];
    dea: { time: Time; value: number }[];
    macd: { time: Time; value: number; color: string }[];
} | null {
    const macd = indicators?.['MACD'];
    if (!macd?.values?.length) return null;
    const normalizedParam = normalizeMacdKey(macdParamKey);
    let macdKey: string | null = null;
    for (const pt of macd.values) {
        if (pt.multiPeriodValues) {
            for (const k of Object.keys(pt.multiPeriodValues)) {
                if (k.startsWith('macd_') && normalizeMacdKey(k) === normalizedParam) {
                    macdKey = k;
                    break;
                }
            }
        }
        if (macdKey) break;
    }
    if (null == macdKey) return null;
    const dif: { time: Time; value: number }[] = [];
    const dea: { time: Time; value: number }[] = [];
    const macdBars: { time: Time; value: number; color: string }[] = [];
    for (const pt of macd.values) {
        const val = pt.multiPeriodValues?.[macdKey];
        if (val && null != val.diff) {
            const t = toUtcSeconds(pt.timestamp) as Time;
            dif.push({time: t, value: val.diff});
            if (null != val.dea) dea.push({time: t, value: val.dea});
            const m = val.macd;
            if (null != m) {
                macdBars.push({time: t, value: m, color: m >= 0 ? 'rgba(0,217,126,0.5)' : 'rgba(255,82,82,0.5)'});
            }
        }
    }
    if (0 === dif.length) return null;
    dif.sort((a, b) => (a.time as number) - (b.time as number));
    dea.sort((a, b) => (a.time as number) - (b.time as number));
    macdBars.sort((a, b) => (a.time as number) - (b.time as number));
    return {dif, dea, macd: macdBars};
}

function indicatorsToRsiData(indicators: any, period: number): { time: Time; value: number }[] {
    return extractIndicatorLineData(indicators, 'RSI', 'rsi', period);
}

/**
 * 去重收集证据链中的全部落点区域（primary.zone + 冲突双方 zones）
 */
function collectZones(signal: CompositeSignal): EvidenceZone[] {
    const byId = new Map<number, EvidenceZone>();
    const push = (z?: EvidenceZone | null) => {
        if (z && !byId.has(z.resultId)) byId.set(z.resultId, z);
    };
    push(signal.evidence?.zone);
    signal.evidence?.bullGroup?.zones?.forEach(push);
    signal.evidence?.bearGroup?.zones?.forEach(push);
    return [...byId.values()];
}

/**
 * 证据标记：主证据箭头+形态名，附加证据圆点；
 * klineEndTime 对齐到最近一根已有K线（形态早于窗口时贴首根显示）
 */
function collectMarkers(signal: CompositeSignal, times: number[]): SeriesMarker<Time>[] {
    const markers: SeriesMarker<Time>[] = [];
    const snap = (ts: number): number | null => {
        const target = toUtcSeconds(ts);
        if (!times.length) return null;
        let best = times[0];
        for (const t of times) {
            if (t <= target) best = t; else break;
        }
        return best;
    };
    const addPattern = (p: {
        resultId: number;
        direction: number;
        patternName: string;
        klineEndTime: number
    }, primary: boolean) => {
        const time = snap(p.klineEndTime);
        if (null == time) return;
        const bull = 1 === p.direction;
        const neutral = 0 === p.direction;
        markers.push({
            time: time as Time,
            position: neutral ? 'inBar' : bull ? 'belowBar' : 'aboveBar',
            color: neutral ? CHART_COLORS.amber : bull ? CHART_COLORS.bull : CHART_COLORS.bear,
            shape: neutral ? 'circle' : bull ? 'arrowUp' : 'arrowDown',
            text: primary ? p.patternName : '',
            size: primary ? 2 : 1,
        });
    };
    const ev = signal.evidence;
    if (ev?.primary) addPattern({...ev.primary, patternName: ev.primary.patternName}, true);
    ev?.extraPatterns?.forEach(p => addPattern(p, false));
    const inGroup = new Set<number>();
    const pushGroup = (group?: {
        patterns?: Array<{ resultId: number; direction: number; patternName: string; klineEndTime: number }>
    } | null) => {
        group?.patterns?.forEach(p => {
            if (inGroup.has(p.resultId)) return;
            inGroup.add(p.resultId);
            addPattern(p, false);
        });
    };
    pushGroup(ev?.bullGroup);
    pushGroup(ev?.bearGroup);
    return markers.sort((a, b) => Number(a.time) - Number(b.time));
}

/**
 * 从 indicatorConfigs 提取 API 请求参数（仅包含勾选的周期）
 */
function buildIndicatorParams(configs: IndicatorItemConfig[]): {
    indicators: string[];
    smaPeriods: number[];
    bollParams: string[];
    rsiPeriods: number[];
    macdPeriods: number[];
} {
    return configs.reduce((acc, ic) => {
        const checked = ic.periods.filter(p => p.checked);
        if (checked.length === 0) return acc;
        acc.indicators.push(ic.key);
        if ('SMA' === ic.key) acc.smaPeriods.push(...checked.map(p => Number(p.value)));
        if ('BOLL' === ic.key) acc.bollParams.push(...checked.map(p => p.value));
        if ('RSI' === ic.key) acc.rsiPeriods.push(...checked.map(p => Number(p.value)));
        if ('MACD' === ic.key) acc.macdPeriods.push(...checked.flatMap(p => p.value.split('_').map(Number)));
        return acc;
    }, {
        indicators: [] as string[],
        smaPeriods: [] as number[],
        bollParams: [] as string[],
        rsiPeriods: [] as number[],
        macdPeriods: [] as number[]
    });
}

export const CompositeKlinePanel: React.FC<CompositeKlinePanelProps> = ({signal, apiKeyId, onClose}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
    const priceLineRef = useRef<any>(null);
    const rtPriceRef = useRef<number | null>(null);

    const [indicatorConfigs, setIndicatorConfigs] = useState<IndicatorItemConfig[]>(DEFAULT_INDICATOR_CONFIGS);
    const [expandedIndKey, setExpandedIndKey] = useState<string | null>(null);
    const indicatorFingerprint = JSON.stringify(indicatorConfigs.map(c => ({
        k: c.key,
        p: c.periods.map(p => p.value + ':' + p.checked),
    })));

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        // ── 图表配置与 PatternRecognitionPage 完全一致 ──
        const chart = createChart(el, {
            layout: {
                attributionLogo: false,
                background: {type: ColorType.Solid, color: CHART_COLORS.bg},
                textColor: CHART_COLORS.text,
            },
            grid: {
                vertLines: {color: CHART_COLORS.grid, style: 3},
                horzLines: {color: CHART_COLORS.grid, style: 3},
            },
            crosshair: {mode: 0},
            rightPriceScale: {borderColor: CHART_COLORS.grid},
            timeScale: {borderColor: CHART_COLORS.grid, timeVisible: true, secondsVisible: false, fixLeftEdge: true},
            handleScroll: {vertTouchDrag: true, horzTouchDrag: false},
            handleScale: {axisDoubleClickReset: false},
            width: el.clientWidth,
            height: el.clientHeight,
        });
        chartRef.current = chart;

        const candleSeries = chart.addSeries(CandlestickSeries, {
            upColor: CHART_COLORS.bull,
            downColor: CHART_COLORS.bear,
            borderUpColor: CHART_COLORS.bull,
            borderDownColor: CHART_COLORS.bear,
            wickUpColor: CHART_COLORS.bull,
            wickDownColor: CHART_COLORS.bear,
            priceLineVisible: false,
            lastValueVisible: false,
        });
        candleRef.current = candleSeries;
        const markersPlugin = createSeriesMarkers(candleSeries, []);

        const volSeries = chart.addSeries(HistogramSeries, {
            priceScaleId: 'vol',
            priceFormat: {type: 'volume'},
            priceLineVisible: false,
            lastValueVisible: false,
        });
        chart.priceScale('vol').applyOptions({scaleMargins: {top: 0.82, bottom: 0}});

        const onResize = () => {
            if (containerRef.current) chart.applyOptions({width: containerRef.current.clientWidth});
        };
        window.addEventListener('resize', onResize);

        let cancelled = false;
        const params = buildIndicatorParams(indicatorConfigs);
        tradingService.getCompleteChartData({
            instId: signal.symbol,
            apiKeyId,
            timeframe: signal.interval,
            limit: 240,
            ...params,
        }).then(data => {
            if (cancelled || !data?.candles?.length) return;
            const sorted = [...data.candles].sort((a, b) => a.timestamp - b.timestamp);
            const times = sorted.map(c => toUtcSeconds(Number(c.timestamp)));
            candleSeries.setData(sorted.map(c => ({
                time: toUtcSeconds(Number(c.timestamp)) as Time,
                open: Number(c.open),
                high: Number(c.high),
                low: Number(c.low),
                close: Number(c.close),
            })));
            volSeries.setData(sorted.map((c, i) => ({
                time: times[i] as Time,
                value: Number(c.volume ?? 0),
                color: Number(c.close) >= Number(c.open) ? CHART_COLORS.volUp : CHART_COLORS.volDown,
            })));

            // 证据叠加：形态标记 + 信号区域（与 pattern 页一致：时段+渐变填充）
            markersPlugin.setMarkers(collectMarkers(signal, times));
            const zones = collectZones(signal);
            const fallbackEndTime = times.length ? times[times.length - 1] : toUtcSeconds(signal.triggerKlineTime || Date.now());
            zones.forEach((zone, zi) => {
                const isSupport = 'LOW' === zone.zoneType;
                const isResistance = 'HIGH' === zone.zoneType;
                const isRange = 'RANGE' === zone.zoneType;
                const startT = toUtcSeconds(zone.zoneStartTime ?? signal.triggerKlineTime) as Time;
                const endT = toUtcSeconds(zone.zoneEndTime ?? fallbackEndTime) as Time;
                // 每个区域根据索引微调透明度，叠加时便于区分
                const alphaOffset = Math.min(0.15 * zi, 0.45);
                if (isRange) {
                    const baseAlpha = Math.min(0.3 + alphaOffset, 0.6);
                    const fillAlpha = Math.min(0.15 + alphaOffset, 0.4);
                    const boxFill = chart.addSeries(BaselineSeries, {
                        baseValue: {type: 'price', price: Number(zone.zoneLower)},
                        relativeGradient: false,
                        topFillColor1: `rgba(255, 140, 0, ${baseAlpha})`,
                        topFillColor2: `rgba(255, 140, 0, ${fillAlpha})`,
                        topLineColor: `rgba(255, 140, 0, ${baseAlpha})`,
                        bottomFillColor1: 'transparent',
                        bottomFillColor2: 'transparent',
                        bottomLineColor: 'transparent',
                        lineWidth: 1, lineStyle: 2,
                        priceLineVisible: false,
                        lastValueVisible: false,
                    });
                    boxFill.setData([
                        {time: startT, value: Number(zone.zoneUpper)},
                        {time: endT, value: Number(zone.zoneUpper)},
                    ]);
                    const upperLine = chart.addSeries(LineSeries, {
                        color: CHART_COLORS.bull, lineWidth: 1, lineStyle: 2,
                        priceLineVisible: false, lastValueVisible: false,
                    });
                    upperLine.setData([
                        {time: startT, value: Number(zone.zoneUpper)},
                        {time: endT, value: Number(zone.zoneUpper)},
                    ]);
                    const lowerLine = chart.addSeries(LineSeries, {
                        color: CHART_COLORS.bear, lineWidth: 1, lineStyle: 2,
                        priceLineVisible: false, lastValueVisible: false,
                    });
                    lowerLine.setData([
                        {time: startT, value: Number(zone.zoneLower)},
                        {time: endT, value: Number(zone.zoneLower)},
                    ]);
                    return;
                }
                if (!isSupport && !isResistance) return;
                const zoneColorVal = isSupport ? '#00d97e' : '#f5a623';
                const alphaHex = Math.round(Math.min(0.3 + alphaOffset, 0.6) * 255).toString(16).padStart(2, '0');
                const alphaHex2 = Math.round(Math.min(0.1 + alphaOffset, 0.35) * 255).toString(16).padStart(2, '0');
                const fill = chart.addSeries(AreaSeries, {
                    lineColor: zoneColorVal,
                    topColor: zoneColorVal + alphaHex,
                    bottomColor: zoneColorVal + alphaHex2,
                    lineWidth: 1, lineStyle: 2,
                    priceLineVisible: false, lastValueVisible: false,
                });
                const zonePrice = Number(zone.zonePrice ?? (isSupport ? zone.zoneLower : zone.zoneUpper));
                fill.setData([
                    {time: startT, value: zonePrice},
                    {time: endT, value: zonePrice},
                ]);
            });

            // ── 技术指标渲染（主图 SMA/BOLL，副图 MACD/RSI） ──
            // 主图指标
            const mainInds = indicatorConfigs.filter(ic => 'main' === ic.pane);
            for (const ic of mainInds) {
                for (const ip of ic.periods) {
                    if (!ip.checked) continue;
                    if ('SMA' === ic.key) {
                        const smaData = extractIndicatorLineData(data.indicators, 'SMA', 'sma', Number(ip.value));
                        if (smaData.length) {
                            const s = chart.addSeries(LineSeries, {color: ip.color, lineWidth: 1});
                            s.setData(smaData);
                            s.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        }
                    } else if ('BOLL' === ic.key) {
                        const boll = indicatorsToBollData(data.indicators, ip.value);
                        if (boll) {
                            const mk = (d: any[], style: number) => {
                                const s = chart.addSeries(LineSeries, {
                                    color: ip.color,
                                    lineWidth: 1,
                                    lineStyle: style as any
                                });
                                s.setData(d);
                                s.applyOptions({priceLineVisible: false, lastValueVisible: false});
                            };
                            mk(boll.upper, 2);
                            mk(boll.middle, 0);
                            mk(boll.lower, 2);
                        }
                    }
                }
            }

            // 副图指标（MACD + RSI 共用 pane 1，与 pattern 页一致）
            const subInds = indicatorConfigs.filter(ic => 'sub' === ic.pane && ic.periods.some(p => p.checked));
            if (subInds.length > 0 && chart.panes().length < 2) {
                chart.addPane();
            }
            const macdPaneIndex = 1;
            const macdCfg = subInds.find(ic => 'MACD' === ic.key);
            if (macdCfg) {
                macdCfg.periods.filter(p => p.checked).forEach(ip => {
                    const macdData = indicatorsToMacdData(data.indicators, `macd_${ip.value}`);
                    if (macdData) {
                        const dif = chart.addSeries(LineSeries, {color: ip.color, lineWidth: 1}, macdPaneIndex);
                        const dea = chart.addSeries(LineSeries, {color: '#f5a623', lineWidth: 1}, macdPaneIndex);
                        const hist = chart.addSeries(HistogramSeries, {priceFormat: {type: 'volume'}}, macdPaneIndex);
                        dif.setData(macdData.dif);
                        dea.setData(macdData.dea);
                        hist.setData(macdData.macd);
                        [dif, dea, hist].forEach(s => s.applyOptions({
                            priceLineVisible: false,
                            lastValueVisible: false
                        }));
                        chart.priceScale('right', macdPaneIndex).applyOptions({
                            scaleMargins: {top: 0.55, bottom: 0.05},
                            autoScale: true,
                        });
                    }
                });
            }
            const rsiCfg = subInds.find(ic => 'RSI' === ic.key);
            if (rsiCfg) {
                rsiCfg.periods.filter(p => p.checked).forEach(ip => {
                    const rsiData = indicatorsToRsiData(data.indicators, Number(ip.value));
                    if (rsiData.length) {
                        const s = chart.addSeries(LineSeries, {
                            color: ip.color,
                            lineWidth: 1,
                            priceScaleId: 'rsi',
                        }, macdPaneIndex);
                        s.setData(rsiData);
                        s.applyOptions({priceLineVisible: false, lastValueVisible: false});
                        chart.priceScale('rsi', macdPaneIndex).applyOptions({
                            scaleMargins: {top: 0.05, bottom: 0.55},
                            visible: true,
                        });
                        s.createPriceLine({
                            price: 70,
                            color: 'rgba(255,82,82,0.4)',
                            lineWidth: 1,
                            lineStyle: 2,
                            axisLabelVisible: false,
                            title: ''
                        });
                        s.createPriceLine({
                            price: 30,
                            color: 'rgba(0,217,126,0.4)',
                            lineWidth: 1,
                            lineStyle: 2,
                            axisLabelVisible: false,
                            title: ''
                        });
                    }
                });
            }

            // 渲染后重放实时价格线
            if (null != rtPriceRef.current) {
                candleSeries.createPriceLine({
                    price: rtPriceRef.current,
                    color: CHART_COLORS.amber,
                    lineWidth: 1,
                    lineVisible: false,
                    lineStyle: 2,
                    axisLabelVisible: true,
                    title: '',
                });
            }
            chart.timeScale().fitContent();
        }).catch(() => {
            // 拉取失败保持空图，面板头部提示由外层处理
        });

        return () => {
            cancelled = true;
            window.removeEventListener('resize', onResize);
            chart.remove();
            chartRef.current = null;
        };
    }, [signal.id, signal.symbol, signal.interval, apiKeyId, indicatorFingerprint]);

    // 实时价格 — 10s 轮询，最新价叠加 price line（与 pattern 页一致）
    useEffect(() => {
        rtPriceRef.current = null;
        let cancelled = false;
        const fetchPrice = () => {
            const priceReq = apiKeyId
                ? getRealtimePrice(signal.symbol, apiKeyId)
                : tradingService.getGlobalDisplayRealtimePrice(signal.symbol);
            priceReq.then(res => {
                if (cancelled) return;
                const data = (res as any)?.data ?? res;
                if (data?.lastPrice) {
                    const price = Number(data.lastPrice);
                    rtPriceRef.current = price;
                    const candleSeries = candleRef.current;
                    if (candleSeries) {
                        if (priceLineRef.current) {
                            candleSeries.removePriceLine(priceLineRef.current);
                            priceLineRef.current = null;
                        }
                        priceLineRef.current = candleSeries.createPriceLine({
                            price,
                            color: CHART_COLORS.amber,
                            lineWidth: 1,
                            lineVisible: false,
                            lineStyle: 2,
                            axisLabelVisible: true,
                            title: '',
                        });
                    }
                }
            }).catch(() => {
            });
        };
        fetchPrice();
        const timer = setInterval(fetchPrice, 10000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [signal.symbol, apiKeyId]);

    const dirText = 1 === signal.direction ? '看多' : -1 === signal.direction ? '看空' : '中性';
    return (
        <div className="cs-chart-panel">
            <div className="cs-chart-head">
                <span className="cs-chart-title">
                    {signal.symbol.replace('-SWAP', '').replace('-USDT', '/USDT')} · {signal.interval}
                    <span className="cs-chart-dir">{dirText}</span>
                </span>
                <span className="cs-chart-hint">证据已叠加：形态标记 + 区域边界</span>
                <button className="cs-action-btn ghost" onClick={onClose}>收起K线图 ▴</button>
            </div>
            {/* 技术指标控制空间（与 pattern 页一致） */}
            <div className="cs-chart-indbar">
                {indicatorConfigs.map(ic => {
                    const checkedCount = ic.periods.filter(p => p.checked).length;
                    const isActive = checkedCount > 0;
                    const isExpanded = expandedIndKey === ic.key;
                    return (
                        <div key={ic.key} className="cs-ind-wrap"
                             onMouseLeave={() => setExpandedIndKey(null)}>
                            <div
                                className={`cs-ind-tag${isActive ? ' active' : ''}`}
                                style={{opacity: isActive ? 1 : 0.4}}
                                onClick={() => {
                                    const allOff = ic.periods.every(p => !p.checked);
                                    setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                        ...c, periods: c.periods.map(p => ({...p, checked: allOff}))
                                    } : c));
                                }}
                            >
                                <span className="cs-ind-dot"
                                      style={{background: isActive ? ic.periods.find(p => p.checked)?.color ?? '#3d5469' : '#3d5469'}}/>
                                <span
                                    className="cs-ind-label">{ic.label}{checkedCount > 0 ? ` +${checkedCount}` : ''}</span>
                                <span className="cs-ind-arrow"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedIndKey(isExpanded ? null : ic.key);
                                      }}
                                >{isExpanded ? '▴' : '▾'}</span>
                            </div>
                            {isExpanded && (
                                <div className="cs-ind-panel">
                                    <div
                                        className="cs-ind-panel-hint">{'main' === ic.pane ? '主图指标' : '副图指标'}</div>
                                    {ic.periods.map((ip, idx) => (
                                        <div key={idx} className="cs-ind-period-row">
                                            <input type="checkbox" checked={ip.checked} onChange={() => {
                                                setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                    ...c,
                                                    periods: c.periods.map((p, i) => i === idx ? {
                                                        ...p,
                                                        checked: !p.checked
                                                    } : p)
                                                } : c));
                                            }}/>
                                            <span className="cs-ind-dot"
                                                  style={{background: ip.checked ? ip.color : '#3d5469'}}/>
                                            {'BOLL' === ic.key ? (
                                                <>
                                                    <span className="cs-ind-period-label">P:</span>
                                                    <InputNumber size="small" min={2} max={200} step={1}
                                                                 value={Number(ip.value.split('_')[0] ?? 20)}
                                                                 onChange={v => {
                                                                     if (null == v) return;
                                                                     const std = ip.value.split('_')[1] ?? '2.0';
                                                                     const newVal = `${v}_${std}`;
                                                                     setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                         ...c,
                                                                         periods: c.periods.map((p, i) => i === idx ? {
                                                                             ...p,
                                                                             value: newVal,
                                                                             label: newVal
                                                                         } : p)
                                                                     } : c));
                                                                 }}
                                                                 style={{width: 55}}/>
                                                    <span className="cs-ind-period-label">σ:</span>
                                                    <InputNumber size="small" min={0.5} max={5} step={0.5}
                                                                 value={Number(ip.value.split('_')[1] ?? 2.0)}
                                                                 onChange={v => {
                                                                     if (null == v) return;
                                                                     const period = ip.value.split('_')[0] ?? '20';
                                                                     const newVal = `${period}_${v.toFixed(1)}`;
                                                                     setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                         ...c,
                                                                         periods: c.periods.map((p, i) => i === idx ? {
                                                                             ...p,
                                                                             value: newVal,
                                                                             label: newVal
                                                                         } : p)
                                                                     } : c));
                                                                 }}
                                                                 style={{width: 50}}/>
                                                </>
                                            ) : 'MACD' === ic.key ? (
                                                <>
                                                    <InputNumber size="small" min={2} max={100} step={1}
                                                                 value={Number(ip.value.split('_')[0] ?? 12)}
                                                                 onChange={v => {
                                                                     if (null == v) return;
                                                                     const parts = ip.value.split('_');
                                                                     const newVal = `${v}_${parts[1] ?? 26}_${parts[2] ?? 9}`;
                                                                     setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                         ...c,
                                                                         periods: c.periods.map((p, i) => i === idx ? {
                                                                             ...p,
                                                                             value: newVal,
                                                                             label: newVal
                                                                         } : p)
                                                                     } : c));
                                                                 }}
                                                                 style={{width: 50}}/>
                                                    <InputNumber size="small" min={2} max={200} step={1}
                                                                 value={Number(ip.value.split('_')[1] ?? 26)}
                                                                 onChange={v => {
                                                                     if (null == v) return;
                                                                     const parts = ip.value.split('_');
                                                                     const newVal = `${parts[0] ?? 12}_${v}_${parts[2] ?? 9}`;
                                                                     setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                         ...c,
                                                                         periods: c.periods.map((p, i) => i === idx ? {
                                                                             ...p,
                                                                             value: newVal,
                                                                             label: newVal
                                                                         } : p)
                                                                     } : c));
                                                                 }}
                                                                 style={{width: 50}}/>
                                                    <InputNumber size="small" min={2} max={100} step={1}
                                                                 value={Number(ip.value.split('_')[2] ?? 9)}
                                                                 onChange={v => {
                                                                     if (null == v) return;
                                                                     const parts = ip.value.split('_');
                                                                     const newVal = `${parts[0] ?? 12}_${parts[1] ?? 26}_${v}`;
                                                                     setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                         ...c,
                                                                         periods: c.periods.map((p, i) => i === idx ? {
                                                                             ...p,
                                                                             value: newVal,
                                                                             label: newVal
                                                                         } : p)
                                                                     } : c));
                                                                 }}
                                                                 style={{width: 50}}/>
                                                </>
                                            ) : (
                                                <InputNumber size="small" min={2} max={200}
                                                             value={Number(ip.value)}
                                                             onChange={v => {
                                                                 if (null == v) return;
                                                                 setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                                     ...c,
                                                                     periods: c.periods.map((p, i) => i === idx ? {
                                                                         ...p,
                                                                         value: String(v),
                                                                         label: String(v)
                                                                     } : p)
                                                                 } : c));
                                                             }}
                                                             style={{width: 60}}/>
                                            )}
                                            <span className="cs-ind-period-del" onClick={() => {
                                                setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                                    ...c, periods: c.periods.filter((_, i) => i !== idx)
                                                } : c));
                                            }}>×</span>
                                        </div>
                                    ))}
                                    <div className="cs-ind-period-add" onClick={() => {
                                        const usedColors = ic.periods.map(p => p.color);
                                        const nextColor = PERIOD_ADD_COLORS.find(c => !usedColors.includes(c)) ?? PERIOD_ADD_COLORS[ic.periods.length % PERIOD_ADD_COLORS.length];
                                        const newVal = 'MACD' === ic.key ? '12_26_9' : 'BOLL' === ic.key ? '20_2.0' : '14';
                                        setIndicatorConfigs(prev => prev.map(c => c.key === ic.key ? {
                                            ...c,
                                            periods: [...c.periods, {
                                                value: newVal,
                                                label: newVal,
                                                checked: true,
                                                color: nextColor
                                            }]
                                        } : c));
                                    }}>+ 新增周期
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div ref={containerRef} className="cs-chart-body"/>
        </div>
    );
};

export default CompositeKlinePanel;
